import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PencilSimple, Trash, PaperPlaneTilt, CalendarBlank, Clock,
  InstagramLogo, LinkedinLogo, FacebookLogo, FolderOpen, ArrowsClockwise, Info
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = { instagram: InstagramLogo, facebook: FacebookLogo, linkedin: LinkedinLogo };
const PLATFORM_COLORS = { instagram: '#E4405F', facebook: '#1877F2', linkedin: '#0A66C2' };

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'rgba(245,240,232,0.3)' },
  scheduled: { label: 'Scheduled', color: '#3b82f6' },
  publishing: { label: 'Publishing', color: '#f59e0b' },
  published: { label: 'Published', color: '#22c55e' },
  partially_published: { label: 'Partial', color: '#f59e0b' },
  failed: { label: 'Failed', color: '#ef4444' },
};

export default function DraftsPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState('drafts');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/api/publishing/drafts`),
        axios.get(`${API}/api/publishing/scheduled`),
      ]);
      setDrafts(dRes.data.drafts || []);
      setScheduled(sRes.data.scheduled || []);
    } catch {}
    setLoading(false);
  };

  const deleteDraft = async (draftId) => {
    setDeleting(draftId);
    try {
      await axios.delete(`${API}/api/publishing/drafts/${draftId}`);
      setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
    } catch {}
    setDeleting(null);
  };

  const cancelScheduled = async (scheduledId) => {
    try {
      await axios.delete(`${API}/api/publishing/schedule/${scheduledId}`);
      await fetchData();
    } catch {}
  };

  const openDraft = (draft) => {
    const params = new URLSearchParams();
    if (draft.master_caption) params.set('caption', draft.master_caption);
    if (draft.content_type) params.set('type', draft.content_type);
    navigate(`/dashboard/publish?${params.toString()}`);
  };

  const items = tab === 'drafts' ? drafts : scheduled;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="drafts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Drafts & Scheduled</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Manage your saved drafts and upcoming scheduled posts.
            </p>
          </div>
          <button onClick={() => navigate('/dashboard/publish')} data-testid="new-draft-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: '#c8f060', color: '#080808' }}>
            <PencilSimple size={14} weight="bold" /> New Post
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {[
            { id: 'drafts', label: 'Drafts', count: drafts.length, icon: PencilSimple },
            { id: 'scheduled', label: 'Scheduled', count: scheduled.length, icon: CalendarBlank },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all"
              style={{
                background: tab === t.id ? 'rgba(200,240,96,0.06)' : 'transparent',
                color: tab === t.id ? '#c8f060' : 'rgba(245,240,232,0.3)',
                border: tab === t.id ? '1px solid rgba(200,240,96,0.15)' : '1px solid transparent',
              }}>
              <t.icon size={13} />
              {t.label}
              <span className="ml-1 text-[10px] opacity-50">({t.count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : tab === 'drafts' ? (
          drafts.length === 0 ? (
            <div className="py-16 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="drafts-empty">
              <FolderOpen size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.08)' }} />
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No drafts yet</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>Drafts are auto-saved when you start composing.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {drafts.map(draft => {
                const statusCfg = STATUS_LABELS[draft.status] || STATUS_LABELS.draft;
                return (
                  <div key={draft.draft_id}
                    className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/[0.02] transition-colors cursor-pointer"
                    style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}
                    onClick={() => openDraft(draft)}
                    data-testid={`draft-${draft.draft_id}`}>
                    <div className="flex -space-x-1">
                      {(draft.selected_platforms || []).map(p => {
                        const Icon = PLATFORM_ICONS[p];
                        return Icon ? <Icon key={p} size={14} weight="fill" style={{ color: PLATFORM_COLORS[p] }} /> : null;
                      })}
                      {(!draft.selected_platforms || draft.selected_platforms.length === 0) && (
                        <PaperPlaneTilt size={14} style={{ color: 'rgba(245,240,232,0.2)' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>
                        {draft.master_caption?.slice(0, 80) || 'Untitled draft'}
                      </p>
                      <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                        Last edited: {new Date(draft.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${statusCfg.color}15`, color: statusCfg.color }}>
                      {statusCfg.label}
                    </span>
                    <button onClick={e => { e.stopPropagation(); deleteDraft(draft.draft_id); }}
                      disabled={deleting === draft.draft_id}
                      className="p-1.5 rounded-lg hover:bg-red-500/10" style={{ color: 'rgba(239,68,68,0.4)' }}>
                      {deleting === draft.draft_id ? <ArrowsClockwise size={12} className="animate-spin" /> : <Trash size={12} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          scheduled.length === 0 ? (
            <div className="py-16 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="scheduled-empty">
              <CalendarBlank size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.08)' }} />
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No scheduled posts</p>
            </div>
          ) : (
            <div className="space-y-2">
              {scheduled.map(post => (
                <div key={post.scheduled_id}
                  className="flex items-center gap-4 px-4 py-3 rounded-xl"
                  style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}
                  data-testid={`scheduled-${post.scheduled_id}`}>
                  <CalendarBlank size={16} style={{ color: '#3b82f6' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: '#f5f0e8' }}>
                      Scheduled for {new Date(post.scheduled_for).toLocaleString()}
                    </p>
                    <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                      {post.timezone} · {post.queue_status}
                    </p>
                  </div>
                  <button onClick={() => cancelScheduled(post.scheduled_id)}
                    className="text-[10px] px-2.5 py-1 rounded-md font-medium"
                    style={{ background: 'rgba(239,68,68,0.06)', color: 'rgba(239,68,68,0.6)', border: '1px solid rgba(239,68,68,0.1)' }}>
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </DashboardLayout>
  );
}
